import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import { ExternalLink, Loader2, CalendarDays, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { PAPER } from './theme';
import { AuthThumb, rowThumbProps } from './AuthThumb';
import { InvoiceDocViewer } from './InvoiceDocViewer';
import { useVatBasis, rowPence, BASIS_LABEL } from '../../components/VatBasisToggle';

const fmtGbp = (pounds) => `£${(Number(pounds) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS_LABEL = { provisional: 'Awaiting invoice', confirmed: 'Confirmed', void: 'Voided' };
const LC_LABEL = { sent: 'sent to LiveCosts', partial: 'partly sent to LiveCosts', failed: 'not sent to LiveCosts', skipped: 'not sent to LiveCosts', pending: 'sending to LiveCosts…' };

function Field({ label, children, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: PAPER.muted }}>{label}</div>
      <div className="text-[14px] mt-0.5" style={{ color: PAPER.ink }} data-testid={testid}>{children}</div>
    </div>
  );
}

/** Read-only detail for one cost row: image, values, notes, who/when. */
export function CostLogDetail({ rowId, diaryPrefix, projectId, onClose }) {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDoc, setShowDoc] = useState(false);
  const [basis] = useVatBasis();

  useEffect(() => {
    if (!rowId) return;
    setLoading(true); setShowDoc(false);
    api.get(`/cost-ledger/entries/${rowId}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [api, rowId]);

  const row = data?.row;
  const capture = data?.capture;
  const inboxItem = data?.inbox_item;
  const hasDoc = !!(inboxItem && row?.inbox_has_file);
  const diaryEntry = data?.diary_entry;
  const notes = Array.isArray(diaryEntry?.notes) ? diaryEntry.notes : [];
  const pdfUrl = capture?.output_pdf?.onedrive_url;
  const created = row?.created_at ? parseISO(row.created_at) : null;

  return (
    <Dialog open={!!rowId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden border-0 max-h-[92vh] flex flex-col"
        style={{ background: PAPER.page, color: PAPER.ink }}
        data-testid="cost-log-detail"
      >
        <DialogTitle className="sr-only">Cost detail</DialogTitle>
        <DialogDescription className="sr-only">Receipt image, value and notes for this material cost.</DialogDescription>
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: PAPER.accent }} /></div>
        ) : !row ? (
          <div className="py-12 text-center text-sm" style={{ color: PAPER.muted }}>Couldn't load this cost.</div>
        ) : (
          <div className="overflow-y-auto">
            <button type="button" onClick={() => hasDoc && setShowDoc(true)} className="w-full block text-left" disabled={!hasDoc} data-testid="cost-log-detail-image-btn">
              <AuthThumb {...rowThumbProps(row, 900)} size="lg" fit="contain" className="w-full h-64 rounded-none border-0" />
            </button>
            <div className="px-5 py-4 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[18px] font-bold truncate" data-testid="cost-log-detail-supplier">{row.supplier_name || 'Unknown supplier'}</div>
                  <div className="text-[12px] flex items-center gap-1.5 flex-wrap" style={{ color: PAPER.inkSoft }}>
                    <span className="uppercase tracking-wider font-bold text-[10px] px-1.5 py-0.5 rounded" style={{ background: '#DBEAFE', color: '#1E40AF' }}>{row.category}</span>
                    <span data-testid="cost-log-detail-status">{STATUS_LABEL[row.status] || row.status}</span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-[20px] font-bold tabular-nums" style={{ color: row.value_pending ? '#92400E' : PAPER.ink }} data-testid="cost-log-detail-value">
                    {row.value_pending ? 'Pending' : fmtGbp(rowPence(row, basis) / 100)}
                  </div>
                  {row.value_pending && <div className="text-[10px]" style={{ color: PAPER.muted }}>value not yet known</div>}
                  {!row.value_pending && (
                    <div className="text-[10px] tabular-nums" style={{ color: PAPER.muted }} data-testid="cost-log-detail-vat">
                      {(row.vat_pence || 0) > 0
                        ? (basis === 'net' ? `ex VAT · + ${fmtGbp(row.vat_pence / 100)} VAT = ${fmtGbp(row.value_pence / 100)}` : `inc VAT · ${fmtGbp(row.net_pence / 100)} ex VAT`)
                        : `${BASIS_LABEL[basis]} · no VAT recorded`}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Reference" testid="cost-log-detail-reference">{row.reference || '—'}</Field>
                <Field label={inboxItem ? 'Invoice date' : 'Receipt date'} testid="cost-log-detail-invoice-date">{row.invoice_date ? format(parseISO(row.invoice_date), 'dd MMM yyyy') : '—'}</Field>
                <Field label={inboxItem ? 'Posted by' : 'Captured by'} testid="cost-log-detail-by">{row.captured_by_name || capture?.user_name || inboxItem?.posted_by_name || '—'}</Field>
                <Field label={inboxItem ? 'Posted' : 'Captured'} testid="cost-log-detail-created">{created ? format(created, 'dd MMM yyyy HH:mm') : '—'}</Field>
              </div>

              {inboxItem && (
                <div className="rounded-md px-3 py-2 text-[12px] flex items-start gap-2" style={{ background: PAPER.paperMuted, color: PAPER.inkSoft }} data-testid="cost-log-detail-inbox-info">
                  <FileText className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: PAPER.accent }} />
                  <span>
                    Supplier invoice posted from the Cost Inbox{inboxItem.lines?.length > 1 ? ` (${inboxItem.lines.length} lines, invoice net ${fmtGbp(inboxItem.net_pounds ?? inboxItem.value_pounds)} ex VAT)` : ''}
                    {inboxItem.livecosts?.status ? ` · ${LC_LABEL[inboxItem.livecosts.status] || inboxItem.livecosts.status}` : ''}
                    {!hasDoc && ' · no file attached'}
                  </span>
                </div>
              )}

              {(capture?.notes || row.tags?.length > 0) && (
                <div className="space-y-2">
                  {capture?.notes && <Field label="Capture note" testid="cost-log-detail-capture-note">{capture.notes}</Field>}
                  {row.tags?.length > 0 && (
                    <div className="flex gap-1.5 flex-wrap">
                      {row.tags.map(t => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: PAPER.paperMuted, color: PAPER.inkSoft }}>#{t}</span>)}
                    </div>
                  )}
                </div>
              )}

              {row.status === 'void' && row.voided_reason && (
                <div className="rounded-md px-3 py-2 text-[12px]" style={{ background: '#FEE2E2', color: '#B91C1C' }} data-testid="cost-log-detail-void-reason">
                  Voided: {row.voided_reason}
                </div>
              )}

              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: PAPER.muted }}>Diary notes</div>
                {notes.length === 0 ? (
                  <div className="text-[12px] italic" style={{ color: PAPER.muted }}>No notes on this entry.</div>
                ) : (
                  <div className="space-y-1.5" data-testid="cost-log-detail-notes">
                    {notes.map(n => (
                      <div key={n.id} className="text-[12px] rounded-md px-2.5 py-1.5" style={{ background: PAPER.paperMuted }}>
                        <div className="text-[13px] whitespace-pre-wrap">{n.text}</div>
                        <div className="text-[10px] mt-0.5" style={{ color: PAPER.muted }}>
                          {n.created_by_name || 'Someone'}{n.created_at ? ' · ' + format(parseISO(n.created_at), 'dd MMM yyyy HH:mm') : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-1">
                {hasDoc && (
                  <button
                    type="button"
                    onClick={() => setShowDoc(true)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 text-[13px] font-semibold py-2.5 rounded-lg"
                    style={{ background: PAPER.navy, color: PAPER.accent }}
                    data-testid="cost-log-detail-view-invoice"
                  >
                    <FileText className="w-4 h-4" /> View invoice{(inboxItem.page_count || 1) > 1 ? ` (${inboxItem.page_count} pages)` : ''}
                  </button>
                )}
                {pdfUrl && (
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 text-[13px] font-semibold py-2.5 rounded-lg"
                    style={{ background: PAPER.navy, color: PAPER.accent }}
                    data-testid="cost-log-detail-open-pdf"
                  >
                    <ExternalLink className="w-4 h-4" /> Open stamped PDF
                  </a>
                )}
                {diaryEntry?.date && (
                  <button
                    type="button"
                    onClick={() => { onClose(); navigate(`${diaryPrefix}/${projectId}`, { state: { seedDate: diaryEntry.date } }); }}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 text-[13px] font-semibold py-2.5 rounded-lg"
                    style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.ink }}
                    data-testid="cost-log-detail-open-diary-day"
                  >
                    <CalendarDays className="w-4 h-4" /> Open diary day
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {showDoc && row && <InvoiceDocViewer row={row} inboxItem={inboxItem} onClose={() => setShowDoc(false)} />}
      </DialogContent>
    </Dialog>
  );
}
